import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconInfoCircle } from '#src/atoms/icons/system/iconInfoCircle.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, HeadlineS } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useCreateOrUpdateFiatWallet } from '#src/hooks/wallets/useCreateOrUpdateFiatWallet.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { glossary } from '#src/lib/glossary.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Update my name',
  },
  description: {
    defaultMessage:
      'We’ll need to verify your new email adress <b>{email}</b> in order to change it',
  },
});

const StyledGraphqlForm = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  gap: var(--double-unit);
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    align-items: stretch;
  }
`;

const Helper = styled(Horizontal).attrs({ gap: 1.5 })`
  border-radius: var(--unit);
  background-color: var(--c-nd-200);
  padding: var(--intermediate-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const UpdateNameDialog = ({ open, onClose }: Props) => {
  const { update } = useCreateOrUpdateFiatWallet();
  const { showNotification } = useSnackNotificationContext();

  const { canDepositAndWithdraw } = useFiatBalance();
  const { up: isTablet } = useScreenSize('tablet');
  const { fiatWalletAccountable } = useCurrentUserContext();
  const { firstName, lastName } = fiatWalletAccountable || {};
  const [updatedFirstName, setUpdatedFirstName] = useState<string>(firstName!);
  const [updatedLastName, setUpdatedLastName] = useState<string>(lastName!);
  const { formatMessage } = useIntlContext();

  if (!firstName || !lastName) return null;
  const nameHasChanged =
    !!updatedFirstName &&
    !!updatedLastName &&
    (firstName !== updatedFirstName || lastName !== updatedLastName);

  const updateNames = async (onResult: (result: any) => void) => {
    if (nameHasChanged) {
      const res = await update({
        firstName: updatedFirstName,
        lastName: updatedLastName,
      });
      onResult(res);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      title={
        <HeadlineS as="h5" className="text-center">
          <FormattedMessage {...messages.title} />
        </HeadlineS>
      }
      fullScreen={!isTablet}
    >
      <StyledGraphqlForm
        onSubmit={(_, onResult) => {
          updateNames(onResult);
        }}
        onSuccess={() => {
          showNotification(
            canDepositAndWithdraw
              ? 'cashWalletNamesUpdatedAskingKycAgain'
              : 'cashWalletNamesUpdatedDefault'
          );
          onClose();
        }}
        render={(Error, SubmitButton) => (
          <>
            {!!canDepositAndWithdraw && (
              <Helper>
                <FontAwesomeIcon icon={iconInfoCircle} />
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="You will be required to upload an ID matching this change." />
                </BodyS>
              </Helper>
            )}
            <TextField
              name="firstName"
              defaultValue={firstName}
              label={formatMessage(glossary.firstName)}
              onChange={e => setUpdatedFirstName(e.target.value)}
            />
            <TextField
              name="lastName"
              defaultValue={lastName}
              label={formatMessage(glossary.lastName)}
              onChange={e => setUpdatedLastName(e.target.value)}
            />
            <Error code />
            <SubmitButton disabled={!nameHasChanged} size="large">
              <FormattedMessage {...glossary.confirm} />
            </SubmitButton>
          </>
        )}
      />
    </Dialog>
  );
};
